import React, { useState } from "react";
import {
    Box,
    Button,
    TextField,
    Typography,
    InputAdornment,
    IconButton,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import * as Yup from "yup";
import { useFormik } from "formik";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { BeatLoader } from "react-spinners";
import axiosInstance from "../../config/configAxios";

const PasswordReset = () => {
    const { token } = useParams();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    const email = searchParams.get("email") || "";

    const formik = useFormik({
        enableReinitialize: true,
        initialValues: {
            email,
            password: "",
            confirmPassword: "",
        },
        validationSchema: Yup.object({
            email: Yup.string().email("Email must be valid").required("Email is required"),
            password: Yup.string()
                .required("Password is required")
                .min(6, "Minimum 6 characters"),
            confirmPassword: Yup.string()
                .oneOf([Yup.ref("password"), null], "Passwords must match")
                .required("Confirm password is required"),
        }),
        onSubmit: async (values, { setSubmitting }) => {
            try {
                setSubmitting(true);
                const response = await axiosInstance.post(`/update-password/${token}`, {
                    password: values.password,
                });
                toast.success(response.data.message);
                navigate("/login");
            } catch (error) {
                toast.error(error.response?.data?.error || "Failed to reset password");
            } finally {
                setSubmitting(false);
            }
        },
    });

    return (
        <Box
            sx={{
                minHeight: "100vh",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                background: "#f4f6f9",
            }}
        >
            <Box
                sx={{
                    width: 380,
                    p: 4,
                    background: "#fff",
                    borderRadius: "10px",
                    boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
                    textAlign: "center",
                }}
            >
                {/* Logo */}
                <Box>
                    <img
                        src="https://images.vexels.com/media/users/3/142789/isolated/preview/2bfb04ad814c4995f0c537c68db5cd0b-multicolor-swirls-circle-logo.png"
                        alt="logo"
                        style={{ width: 100, margin: "0 auto", display: "block" }}
                    />
                </Box>

                {/* Title */}
                <Typography fontWeight="bold" fontSize={18}>
                    Reset Your Password
                </Typography>

                <Typography fontSize={13} color="text.secondary" mb={2}>
                    Enter your new password
                </Typography>

                <form onSubmit={formik.handleSubmit}>
                    {/* Email */}
                    <TextField
                        fullWidth
                        size="small"
                        name="email"
                        placeholder="Email"
                        value={formik.values.email}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={formik.touched.email && Boolean(formik.errors.email)}
                        helperText={formik.touched.email && formik.errors.email}
                        FormHelperTextProps={{
                            sx: {
                                marginLeft: "0px",
                            },
                        }}
                        sx={{ mb: 2 }}
                      

                    />

                    {/* Password */}
                    <TextField
                        fullWidth
                        size="small"
                        name="password"
                        type={showPassword ? "text" : "password"}
                        placeholder="New Password"
                        value={formik.values.password}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={formik.touched.password && Boolean(formik.errors.password)}
                        helperText={formik.touched.password && formik.errors.password}
                        FormHelperTextProps={{
                            sx: {
                                marginLeft: "0px",
                            },
                        }}
                        sx={{ mb: 2 }}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton onClick={() => setShowPassword(!showPassword)}>
                                        {showPassword ? (
                                            <VisibilityOff fontSize="small" />
                                        ) : (
                                            <Visibility fontSize="small" />
                                        )}
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                    />

                    {/* Confirm Password */}
                    <TextField
                        fullWidth
                        size="small"
                        name="confirmPassword"
                        type={showConfirmPassword ? "text" : "password"}
                        placeholder="Confirm Password"
                        value={formik.values.confirmPassword}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={formik.touched.confirmPassword && Boolean(formik.errors.confirmPassword)}
                        helperText={formik.touched.confirmPassword && formik.errors.confirmPassword}
                        FormHelperTextProps={{
                            sx: {
                                marginLeft: "0px",
                            },
                        }}
                        sx={{ mb: 2 }}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton onClick={() => setShowConfirmPassword(!showConfirmPassword)}>
                                        {showConfirmPassword ? (
                                            <VisibilityOff fontSize="small" />
                                        ) : (
                                            <Visibility fontSize="small" />
                                        )}
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                    />

                    {/* Button */}
                    <Button
                        type="submit"
                        fullWidth
                        disabled={formik.isSubmitting}
                        sx={{
                            background: "#1976d2",
                            textTransform: "none",
                            py: 1,
                            fontWeight: "bold",
                            color: "#fff",
                            "&:hover": {
                                background: "#1565c0",
                            },
                            "&.Mui-disabled": {
                                background: "#1976d2",
                                color: "#fff",
                                opacity: 0.7,
                            },
                        }}
                    >
                        {formik.isSubmitting ? (
                            <BeatLoader color="#fff" size={8} />
                        ) : (
                            "Reset Password"
                        )}
                    </Button>
                </form>
            </Box>
        </Box>
    );
};

export default PasswordReset;
